import { eventbus } from "@/eventbus";
import { BaseCrudService } from "@/services/base";
import { User, UserUpdateDTO } from "@/swagger/models";
import { AxiosRequestConfig } from "axios";

export interface TempUsersSearchDTO {
    customer: number;
    name?: string; // this is a trimmed, composite, greedy, case insensitive param
    withMaxReservations?: Boolean;
}

export type UserCreateDTO = Partial<User & { activate?: boolean }>;

class UserService extends BaseCrudService<User, UserCreateDTO, UserUpdateDTO> {
    constructor() {
        super({
            apiPrefix: "users",
            entityName: "user",
        });
    }

    build(fields: Partial<User> & Pick<User, "customerId">): User {
        return Object.assign(
            {
                firstName: "",
                lastName: "",
                title: "",
                email: "",
                language: "EN",
            },
            fields,
        );
    }

    async search(params: TempUsersSearchDTO) {
        try {
            const res = await this.axios.get<User[]>(`/search`, { params });
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not perform search: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }

    async setRole(userId: number, role: string, successMessage: boolean = true) {
        try {
            const res = await this.axios.put(`/${userId}/role`, {
                role,
            });
            if (successMessage) this.tosti.success({ message: `Updated role for ${this.config.entityName}` });
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not set role on ${this.config.entityName}: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }

    async activate(userId: number, activate: boolean, successMessage: boolean = true) {
        try {
            const res = await this.axios.put(`/${userId}/activate`, {
                activate,
            });
            if (successMessage && activate) this.tosti.success({ message: `User activated` });
            if (successMessage && !activate) this.tosti.success({ message: `User deactivated` });
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not set (de)activate user: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }

    async suspend(userId: number, suspend: boolean, successMessage: boolean = true) {
        try {
            const res = await this.axios.put(`/${userId}/suspend`, {
                suspend,
            });
            if (successMessage && suspend) this.tosti.success({ message: `User suspended` });
            if (successMessage && !suspend) this.tosti.success({ message: `User unsuspended` });
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not set (de)suspend user: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }

    async resetPassword(userId: number, successMessage: boolean = true) {
        try {
            const res = await this.axios.post(`/${userId}/reset-password`, {});
            this.tosti.success({ message: `Password reset email has been sent` });
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not reset password: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }

    async unlock(userId: number, successMessage: boolean = true) {
        try {
            const res = await this.axios.post(`/${userId}/unlock`, {});
            this.tosti.success({ message: `User has been unlocked` });
            return res.data;
        } catch (e) {
            this.tosti.error({
                message: `Could not unlock user: ${e.response?.data?.message || e.message}`,
            });
            throw e;
        }
    }
}

export const userService = new UserService();
